<template>
  <router-view v-if="validToken" />
  <div v-else class="flex h-full w-full flex-col items-center justify-center">
    <h1 class="text-center text-2xl">Invalid token</h1>
    <a v-if="store.goBackLink" target="_self" :href="store.goBackLink" class="flex items-center">
      <i class="pi pi-angle-left text-xs" />
      <h2 v-if="store.goBackName" class="m-0 text-center text-sm underline">{{ $t("go back to", { name: store.goBackName }) }}</h2>
    </a>
  </div>
  <Toast position="bottom-right" group="br" />
  <DynamicDialog />
</template>
<script setup lang="ts">
import { useMainStore } from "./store/main.store";
import { changeBackgroundImageColor } from "./utils/color.utils";
import { useThemeStore } from "./store/theme.store";
import { watch, onBeforeMount, ref, onMounted } from "vue";
import { useToast } from "primevue/usetoast";
import Toast from "primevue/toast";
import DynamicDialog from "primevue/dynamicdialog";
import { useDark } from "@vueuse/core";
import { version } from "../package.json";

import "dayjs/locale/es";
import "dayjs/locale/en";
import "dayjs/locale/ca";
import "dayjs/locale/it";
import { isEmpty } from "lodash";
import { setLocale } from "./utils/locale.utils";
import { usePrimeVue } from "primevue";

const isDark = useDark();
const store = useMainStore();
const themeStore = useThemeStore();
const validToken = ref(true);
store.getUserToken();
if (isEmpty(store.preferedLanguage)) store.setDefaultLanguage();
const primevue = usePrimeVue();
setLocale(store.preferedLanguage, primevue);
const toast = useToast();

onBeforeMount(async () => {
  useDark({ valueLight: "light", valueDark: "dark" });
  if (themeStore.primaryColor) themeStore.changePrimaryColor(themeStore.primaryColor);
  if (themeStore.surfaceColor) themeStore.changeSurfaceColor(themeStore.surfaceColor);

  // const uri = window.location.search.substring(1);
  // const token = new URLSearchParams(uri).get("token");
  // if (token) {
  //   try {
  //     const res = await validateToken(token);
  //     if (res && res.data && res.data.isValid) {
  //       await makeLogin(token, res.data.userId, false);
  //       router.push("/");
  //     } else {
  //       validToken.value = false;
  //     }
  //   } catch (error) {
  //     validToken.value = false;
  //   }
  // }
});

onMounted(() => {
  console.log(`Welcome to Inèdit Connect version: ${version}`);
  changeBackgroundImageColor(isDark.value);
});

watch(
  () => store.errorMessage,

  (message, _prevMessage) => {
    if (message) {
      toast.add({
        severity: "error",
        summary: "Error",
        detail: message,
        group: "br",
        life: 6000,
      });
    }
  },
);
watch(
  () => store.toastMessage,

  (toastMessage: any, _prevValue) => {
    if (toastMessage.message) {
      if (!toastMessage.severity || (toastMessage.severity != "success" && toastMessage.severity != "error")) toastMessage.severity = "warn";
      toast.add({
        severity: toastMessage.severity,
        detail: toastMessage.message,
        group: "br",
        life: 6000,
      });
    }
  },
);
</script>

<style lang="scss">
html {
  height: 100%;
}
:root {
  --main-color: 215deg;
  --first-color: hsla(var(--main-color), 34%, 14%, 1);
  --second-color: hsla(var(--main-color), 35%, 19%, 1);
  --third-color: hsla(var(--main-color), 35%, 29%, 1);
  --accent-color-base: "46deg, 100%, 65%";
  --accent-color: hsla(var(--accent-color-base), 1);
  --second-accent-color: hsla(22, 83%, 58%, 1);
}
#app {
  font-family:
    Nunito Sans,
    Noto Sans,
    Lato,
    DM Sans,
    Helvetica,
    Arial,
    sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  height: 100%;
}

#nav {
  padding: 30px;

  a {
    font-weight: bold;
    color: #2c3e50;

    &.router-link-exact-active {
      color: #42b983;
    }
  }
}
body {
  margin: 0;
  height: 100%;
  overflow-x: hidden;
  overflow-y: hidden;
  //background-color: var(--first-color);
  font-family: var(--font-family);
  font-weight: 400;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;

  background-attachment: fixed;
  background-size: cover;
  /* background by SVGBackgrounds.com */
}
.dark body {
  background-attachment: fixed;
  background-size: cover;
  /* background by SVGBackgrounds.com */
}
a {
  text-decoration: none;
  cursor: pointer;
}

span.p-row-editor-save-icon {
  color: #c5e1a5 !important ;
}

button.p-row-editor-save.p-link:focus {
  box-shadow: 0 0 0 1px #c5e1a5 !important;
}
button.p-row-editor-save.p-link:hover > span {
  color: #b2d788 !important;
}
button.p-row-editor-save.p-link:hover {
  background: hsl(88deg 50% 69% / 15%) !important;
}
button.p-row-editor-cancel.p-link:hover {
  background: hsl(0deg 66% 65% / 15%) !important;
}
span.p-row-editor-cancel-icon {
  color: #f16c98 !important ;
}

button.p-row-editor-cancel.p-link:focus {
  box-shadow: 0 0 0 1px #f16c98 !important;
}

::-webkit-scrollbar {
  width: 6px;
  height: 7px;
}

.dark ::-webkit-scrollbar-track {
  background: var(--p-surface-800);
  border-radius: 4px;
}

.dark ::-webkit-scrollbar-thumb {
  border-radius: 10px;
  background: var(--p-surface-600);
}
::-webkit-scrollbar-track {
  background: var(--p-surface-200);
  border-radius: 4px;
}

::-webkit-scrollbar-thumb {
  border-radius: 10px;
  background: var(--p-surface-300);
}
::selection {
  background: var(--p-primary-500);
  color: var(--primary-color-text);
}
.p-tag-value {
  white-space: nowrap;
}
@media screen and (max-width: 600px) {
  .p-column-filter {
    font-size: 13px;
  }
}
input {
  font-size: 0.875rem !important;
}
.p-select-option-label,
.p-select-label {
  font-size: 0.875rem !important;
}
svg {
  shape-rendering: geometricPrecision;
  text-rendering: geometricPrecision;
  image-rendering: optimizeQuality;
}
</style>
